import { IHttpResponse, IHttpService, IPromise } from 'angular';
import * as _ from 'underscore';
import { toNativePromise } from '~commonInfra/utils/QUtils';
import { Content } from '../../../../content/resources/Content';
import { IResult } from '../../../../httpClient/serverApi/types/IResponse';
import WebHttpClient from '../../../../httpClient/WebHttpClient';
import { FeatureFlags } from '../../../../ts/FeatureFlags';

import { ListResult } from '../../../../ts/fetchers/ListResult';
import { ServerFilterClauses } from '../../../../ts/helpers/ServerFilterClauses';
import { CollectionMapper } from '../../../../ts/mappers/CollectionMapper';
import { BaseParamsBuilderResourceQuery } from '../../../../ts/resources/ResourceQuery';
import { MarkViewedRequest, ViewedObjectType } from '../../../../ts/ServerApi/requests/MarkViewedRequest';
import {
  FilterFieldNames,
  FilterOps,
  IFieldFilterClause,
  ILeafFilterClause,
  IStringValuedFieldFilterClause,
  IStringValuedFilterClause,
  IValuedFieldFilterClause,
} from '../../../../ts/ServerApi/types/IFilterClause';
import { IOrderClause } from '../../../../ts/ServerApi/types/IOrderClause';
import { containsFilterClause, removeFilterClauseFromFilters } from '../../../actions/spaces/filters/FilterHelpers';
import { ServiceLocator } from '../../../ServiceLocator';
import {
  Collection,
  CollectionError,
  CollectionVisibility,
  DeleteCollectionData,
  UpdateCollectionsResponseData,
} from '../CollectionModels';
import { ICollectionInfo } from '../ICollectionInfo';
import { IGetCollectionResult } from '../IGetCollectionResult';
import { CollectionsListResponse, collectionsRequestConfig, COLLECTIONS_PATH, FilterOpsToString } from './CollectionResourceQueryHelpers';

export interface GetCollectionResponse {
  readonly collection: Collection;
  readonly errors: CollectionError[];
}

export interface GetCollectionsResponse extends CollectionsListResponse<Collection> {
}

export interface UpdateCollectionData {
  readonly luid: string;
  readonly name?: string;
  readonly description?: string;
  readonly visibility?: CollectionVisibility;
  readonly ownerLuid?: string;
}

const DELETE_COLLECTIONS_BLOCK_SIZE = 50;
export interface DeleteResponse {
  readonly response: string[];
  readonly errors: CollectionError[];
}

export interface UpdateResponse {
  readonly response: string[];
  readonly errors: CollectionError[];
}

function replaceIsFavoriteFilterForCollections(filters: ILeafFilterClause[]): ILeafFilterClause[] {
  if (!filters || filters.length === 0) {
    return filters;
  }

  let isFavoriteFilterFn: (clause: ILeafFilterClause)  => boolean =
    (clause: ILeafFilterClause) =>
      clause.operator === FilterOps.eq && (clause as IFieldFilterClause).field === FilterFieldNames.isFavorite;

  let isFavoriteFilter = filters.find((clause) => isFavoriteFilterFn(clause)) as IValuedFieldFilterClause<boolean>;
  if (!isFavoriteFilter) {
    // didn't find a isFavorites filter so no manipulation needed
    return filters;
  }

  // remove isFavorite
  let updatedFilters: ILeafFilterClause[] = filters.filter((clause) => !isFavoriteFilterFn(clause));
  // add it back in but correct for collections
  updatedFilters.push(ServerFilterClauses.serverFilterClauses.favoriteCollectionsFilter(isFavoriteFilter.value + ''));
  return updatedFilters;
}

export class CollectionsResourceQuery extends BaseParamsBuilderResourceQuery<ICollectionInfo> {
  public getSlice(start: number, size: number): Promise<ListResult<ICollectionInfo>> {
    const http = ServiceLocator.get<IHttpService>('$http');

    let filters: ILeafFilterClause[] = this.getFilters();
    const matchesFilter = filters.find((clause) => clause.operator === FilterOps.matches);

    // If favorites is being powered by collections, and collectionsGA is not enabled, and we have a search and favorites filter
    // we can't go to CS because it doesnt support search, and we cant go to CRASS because it doesnt support favorites
    // So exploreFavoriteContentForUser it is.
    // If collectionsGA is enabled, search is supported in CS, so this special case is no longer needed.
    if (FeatureFlags.useCollectionsFavorites && !FeatureFlags.collectionsGAEnabled && matchesFilter && containsFilterClause(filters, FilterFieldNames.isFavorite)) {
      const filtersUpdated = removeFilterClauseFromFilters(filters, FilterFieldNames.isFavorite);
      filtersUpdated.push(ServerFilterClauses.ContentType(['collection']));
      return Content.forFavoritesFromCRASS(filtersUpdated).withOrder(this.getOrder())
        .getSlice(start, size) as Promise<ListResult<ICollectionInfo>>;
    }

    // If we are going to Collections Service, 'isFavorite' needs to be replaced with 'favorite' to conform to AIPs
    filters = replaceIsFavoriteFilterForCollections(filters);

    let queryParams: {search?: string, sort?: string, filter?: string, start_index?: string, max_items?: string} = {};

    const order: IOrderClause[] = this.getOrder();
    if (order.length > 0) {
      queryParams.sort = order
          .map((clause) => clause.field + ' ' + (clause.ascending ? 'asc' : 'desc'))
          .join(',');
    }

    if (filters.length > 0) {
      const filtersWithoutMatches = _.without(filters, matchesFilter);
      if (filtersWithoutMatches.length > 0) {
        queryParams.filter =
          filtersWithoutMatches
            .map((clause) => {
              const valueClause = clause as IStringValuedFieldFilterClause;
              return valueClause.field + FilterOpsToString(valueClause.operator) + '"' + valueClause.value + '"';
            })
            .join(' AND ');
      }
    }

    if (FeatureFlags.collectionsGAEnabled && matchesFilter) {
      const searchValue = encodeURIComponent((matchesFilter as IStringValuedFilterClause).value);
      if (searchValue) {
        queryParams.search = searchValue;
      }
    }

    if (start != null) {
      queryParams.start_index = '' + start;
    }
    if (size != null) {
      queryParams.max_items = '' + size;
    }

    let queryString = '';
    if (!_.isEmpty(queryParams)) {
      queryString = '?';
      queryString += _.pairs(queryParams).map((value) => value[0]+'='+value[1]).join('&');
    }

    const url = COLLECTIONS_PATH + queryString;

    const requestConfig = collectionsRequestConfig('GET', url);

    return toNativePromise(http(requestConfig)
      .then((response: IHttpResponse<GetCollectionsResponse>) => {
        return CollectionMapper.mapList(response, start);
      })
      .catch(() => {
        return ListResult.empty<ICollectionInfo>();
      }));
  }

  public addDelay<T>(result: ng.IPromise<T>): ng.IPromise<T> {
    const timeout = ServiceLocator.get<ng.ITimeoutService>('$timeout');
    return result.then((value: T) => timeout(() => value, 2000));
  }


  public create(name: string): ng.IPromise<ICollectionInfo> {
    const http = ServiceLocator.get<IHttpService>('$http');
    const requestConfig = collectionsRequestConfig('POST', COLLECTIONS_PATH, { name, desc: '' });
    return http(requestConfig).then(CollectionMapper.mapsSingle);
  }

  private deleteCollectionsBlock = DELETE_COLLECTIONS_BLOCK_SIZE;
  public forTestOnly_setDeleteCollectionsBlock(block: number) {
    // Meant for unit tests only
    this.deleteCollectionsBlock = block;
    return this;
  }

  public delete(luids: string[]): Promise<DeleteCollectionData> {
    const http = ServiceLocator.get<IHttpService>('$http');

    const promises = _.chunk(luids, this.deleteCollectionsBlock).map((luidsChunk: string[]) => {
      return this.deleteHelper(luidsChunk, http);
    });

    const allPromises = Promise.all(promises).then((results: DeleteCollectionData[]) => {
      return _.reduce(results, (memo, result: DeleteCollectionData) => {
        return {
          errors: memo.errors.concat(result.errors),
          successfulLuids: memo.successfulLuids.concat(result.successfulLuids)
        };
      }, {errors: [], successfulLuids: []});
    });

    //adds a 2000 ms delay for deleting so the backend has time to catch up
    return toNativePromise(this.addDelay(allPromises));
  }

  private deleteHelper(luids: string[], http: IHttpService): ng.IPromise<DeleteCollectionData>  {
    const luidsParam = luids.join(',');

    const url = COLLECTIONS_PATH + '?luids=' + luidsParam;
    const requestConfig = collectionsRequestConfig('DELETE', url);

    return http(requestConfig).then((response: IHttpResponse<DeleteResponse>) => {
      return CollectionMapper.mapDeleteCollectionsResponse(response);
    });
  }

  public update(updateData: UpdateCollectionData[]): Promise<UpdateCollectionsResponseData> {
    const http = ServiceLocator.get<IHttpService>('$http');

    const url = COLLECTIONS_PATH + '/batchUpdate';
    const requestConfig = collectionsRequestConfig('POST', url, updateData);

    const responsePromise = http(requestConfig).then((response: IHttpResponse<UpdateResponse>) => {
      return CollectionMapper.mapUpdateCollectionsResponse(response);
    });

    //adds a 2000 ms delay for updating name, description, and owner luid so the backend has time to catch up
    if(updateData[0].description || updateData[0].name || updateData[0].ownerLuid) {
      return toNativePromise(this.addDelay(responsePromise));
    }

    return toNativePromise(responsePromise);
  }

  public getByLuid(luid: string): ng.IPromise<IGetCollectionResult> {
    const http = ServiceLocator.get<IHttpService>('$http');

    const url = COLLECTIONS_PATH + '/' + luid;
    const requestConfig = collectionsRequestConfig('GET', url);

    return http(requestConfig)
      .then(CollectionMapper.mapGetCollectionResult);
  }

  public getCount(): ng.IPromise<number> {
    return this.getSlice(0, 0).then((slice: ListResult<ICollectionInfo>) => slice.total);
  }

  public getFirst(): ng.IPromise<ICollectionInfo> {
    throw new Error('CollectionsResourceQuery getFirst not implemented');
  }
}

export class CollectionsUsageStats {
  public static markViewed(id: string): IPromise<IResult> {
    return WebHttpClient.sendRequest(new MarkViewedRequest({
      objectId: id,
      objectType: ViewedObjectType.Collection
    }));
  }
}
